import { useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  AssignmentModel,
  AssignmentStatusEnum,
  CaseModel,
  CaseStatusEnum,
} from 'api/model';
import { useCaseCreateCase } from 'api/case/case';
import Modal, { modalContentClass } from 'components/Modal';
import Button from 'components/inputs/Button';
import Checkbox from 'components/inputs/Checkbox';
import DatePicker, { DateRangeInput } from 'components/inputs/DatePicker';
import LabelWrap from 'components/inputs/LabelWrap';
import AutoSizedTextArea from 'components/inputs/AutoSizedTextArea';
import useCompanies from 'contexts/basicData/useCompanies';
import { inputStyle } from 'components/inputs/Input';
import { getDefaultAssignment } from 'components/DetailedCase/DetailedCaseFormProvider/useCaseAssignmentForms';
import {
  caseTypeNeverHasFixedTime,
  copyOverMultipleDaysIncludesWeekends,
} from 'components/DetailedCase/CaseAssignments/utils';
import { adjustDateToNoon } from 'utils/date-helpers';

const MyModal = styled(Modal)`
  .${modalContentClass} {
    min-width: 460px;
    max-width: 460px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const Info = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const ResultText = styled.div<{ negative?: boolean }>`
  font-size: 13px;
  color: ${({ theme, negative }) =>
    negative ? theme.colors.foreground.error : theme.colors.foreground.good};
`;

const NumberInput = styled.input`
  ${inputStyle}
  width: 120px;
`;

type Props = {
  assignment: AssignmentModel;
  caseModel: CaseModel;
  onClose: () => void;
};

/** Hela kalenderdagar från `from` till `to` (lokal tid, oberoende av klockslag och sommartid). */
export const calendarDaysBetween = (from: Date, to: Date): number => {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
};

/** `date` flyttad `days` kalenderdagar framåt med samma klockslag. */
export const shiftDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * "Kopiera över flera dagar" för Uthyrd personal, Samåkning, Däckkörning och
 * Biltransport lastbil: skapar ett valbart antal kopior av uppdraget per dag i ett valt
 * datumintervall. Varje kopia är ett nytt ärende bokat på respektive dag, med
 * samma adress(er), arbetstid, estimerad tid och "Information från Ride" som originalet
 * (Däckkörning utan fast tid) — men UTAN fälttestare, så att
 * flera kopior samma dag inte ger schemakrockar (FT planeras in separat).
 * Från-/till-adressen kopieras rakt av från originalet: Uthyrd personal har
 * to == from (arbetsplats), Samåkning har distinkt från/till som bevaras.
 * Uthyrd personal & Samåkning kopieras enbart till vardagar (mån–fre);
 * Biltransport lastbil och Däckkörning kopieras till alla dagar inklusive helger.
 *
 * "Kopiera samtliga uppdrag i ärendet" (förvalt när ärendet har fler ben) ger varje
 * kopia alla ben i originalärendet utom avbokade/borttagna, flyttade lika många dagar
 * som det klickade benet — ett ben dagen efter hamnar alltså dagen efter även i kopian.
 */
const CopyOverMultipleDaysModal: React.FC<Props> = ({
  assignment,
  caseModel,
  onClose,
}) => {
  const createCase = useCaseCreateCase();
  const { companiesById } = useCompanies();

  // Kopiorna ärver originalets företag. Är det en Zäkra-hub kan de nya ärendena inte faktureras,
  // och backend kräver då en förklaring per skapat ärende — se CaseLogic.GetZakraHubExplanation.
  // Förklaringen anges en gång här och stämplas på varje kopia.
  const isZakraHubCase =
    caseModel.companyID !== undefined &&
    companiesById.get(caseModel.companyID)?.isZakraHub === true;

  const includeWeekends = copyOverMultipleDaysIncludesWeekends(
    caseModel.caseTypeID
  );

  const bookedTo = useMemo(
    () => new Date(assignment.bookedTo),
    [assignment.bookedTo]
  );

  const [range, setRange] = useState<[Date | null, Date | null]>([
    adjustDateToNoon(bookedTo),
    adjustDateToNoon(bookedTo),
  ]);
  const [copiesPerDay, setCopiesPerDay] = useState(1);

  // Ben som följer med när "Kopiera samtliga uppdrag i ärendet" är ibockad: alla utom
  // avbokade/avbrutna/borttagna (status från CanceledNotFinished och uppåt), i tidsordning.
  // Det klickade benet tas alltid med.
  const legsToCopyAll = useMemo(
    () =>
      (caseModel.assignmentList ?? [])
        .filter(
          (a) =>
            a.assignmentID === assignment.assignmentID ||
            a.assignmentStatusID < AssignmentStatusEnum.CanceledNotFinished
        )
        .sort(
          (a, b) =>
            new Date(a.bookedTo).getTime() - new Date(b.bookedTo).getTime()
        ),
    [caseModel.assignmentList, assignment.assignmentID]
  );
  const hasMultipleLegs = legsToCopyAll.length > 1;
  const [copyAllLegs, setCopyAllLegs] = useState(true);
  const legsToCopy =
    copyAllLegs && hasMultipleLegs ? legsToCopyAll : [assignment];
  const [zakraHubReason, setZakraHubReason] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [result, setResult] = useState<{
    message: string;
    negative?: boolean;
  } | null>(null);

  // The target days in the selected, inclusive interval — all days for
  // Biltransport lastbil and Däckkörning, weekdays (Mon–Fri) otherwise.
  const targetDays = useMemo<Date[]>(() => {
    const [from, to] = range;
    if (!from) return [];
    const end = to ?? from;
    if (end < from) return [];

    const days: Date[] = [];
    const cursor = new Date(from);
    while (cursor <= end) {
      const weekday = cursor.getDay(); // 0 = Sun, 6 = Sat
      if (includeWeekends || (weekday !== 0 && weekday !== 6)) {
        days.push(new Date(cursor));
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [range, includeWeekends]);

  const totalCopies = targetDays.length * copiesPerDay;

  const buildAssignmentForDay = (leg: AssignmentModel, day: Date) => {
    // Alla ben flyttas lika många kalenderdagar som det klickade benet flyttas till `day`.
    const dayShift = calendarDaysBetween(bookedTo, day);
    // Däckkörning har aldrig fast tid (backend rensar den ändå). Saknar originalet estimerad tid
    // men har en äldre fast tid, blir den kopians estimat så att kopian går att planera.
    const neverFixedTime = caseTypeNeverHasFixedTime(caseModel.caseTypeID);
    const estimatedDuration =
      leg.estimatedDuration ??
      (neverFixedTime && leg.fixedAssignmentTime
        ? leg.fixedAssignmentTime +
          Math.round((leg.breakTime ?? 0) * 60 * 1000)
        : undefined);
    return {
      ...getDefaultAssignment(),
      // Skapas alltid utan fälttestare (status Created) — flera kopior samma dag
      // skulle annars ge schemakrockar för en redan tilldelad FT.
      assignmentStatusID: AssignmentStatusEnum.Created,
      assignmentTypeID: leg.assignmentTypeID,
      // autoApprove medvetet inte kopierad — kopiorna är nya uppdrag och ska få
      // defaulten true, inte ärva källans ev. avgodkännande-reset.
      bookedTo: shiftDays(new Date(leg.bookedTo), dayShift),
      breakTime: leg.breakTime ?? 0,
      estimatedDuration,
      estimatedStartTime: leg.estimatedStartTime
        ? shiftDays(new Date(leg.estimatedStartTime), dayShift)
        : undefined,
      systemEstimatedDurationApprox: leg.systemEstimatedDurationApprox,
      systemEstimatedDurationExact: leg.systemEstimatedDurationExact,
      expectedExpenses: leg.expectedExpenses,
      // "Information från Ride" följer med — kopiorna är samma jobb på andra dagar (till skillnad
      // från den vanliga kopieringen, där fältet hör till originalets bokning).
      extraInfo: leg.extraInfo,
      // Kopiera adress(er) rakt av från originalet. Uthyrd personal: to == from
      // (arbetsplats). Samåkning: distinkt från-/till-adress bevaras.
      fromName: leg.fromName,
      fromAddress: leg.fromAddress,
      fromCity: leg.fromCity,
      fromZip: leg.fromZip,
      fromCounty: leg.fromCounty,
      toName: leg.toName,
      toAddress: leg.toAddress,
      toCity: leg.toCity,
      toZip: leg.toZip,
      toCounty: leg.toCounty,
      fixedAssignmentTime: neverFixedTime ? undefined : leg.fixedAssignmentTime,
      allowPrivateStopOver: leg.allowPrivateStopOver,
      // Ingen fälttestare på kopiorna (getDefaultAssignment ger assignedTo: undefined).
      sendCustomerUpdateMessages: false,
    };
  };

  const buildCaseForDay = (day: Date): CaseModel => {
    return {
      ...structuredClone(caseModel),
      caseID: -1,
      // Kopior är alltid nya, öppna ärenden — ärv inte status från originalet
      // (som kan vara stängt/klart).
      caseStatusID: CaseStatusEnum.Open,
      closedDate: undefined,
      completedDate: undefined,
      rideOrderID: undefined,
      invoiceDataID: undefined,
      isDirectBooking: undefined,
      originalFromName: undefined,
      originalFromAddress: undefined,
      originalFromCity: undefined,
      originalFromZip: undefined,
      originalFromCounty: undefined,
      originalToName: undefined,
      originalToAddress: undefined,
      originalToCity: undefined,
      originalToZip: undefined,
      originalToCounty: undefined,
      zakraHubReason: isZakraHubCase ? zakraHubReason.trim() : undefined,
      assignmentList: legsToCopy.map((leg) => buildAssignmentForDay(leg, day)),
    } as unknown as CaseModel;
  };

  const handleCreate = async () => {
    if (totalCopies === 0) return;
    if (isZakraHubCase && !zakraHubReason.trim()) {
      setResult({
        message: 'Ange varför en Zäkra-hub är satt som företag.',
        negative: true,
      });
      return;
    }
    setResult(null);
    setIsCreating(true);

    let created = 0;
    try {
      // Sequentially so we don't overload the backend and can report progress.
      for (const day of targetDays) {
        for (let n = 0; n < copiesPerDay; n += 1) {
          // eslint-disable-next-line no-await-in-loop
          await createCase.mutateAsync({ data: buildCaseForDay(day) });
          created += 1;
        }
      }
      setResult({
        message: `${created} ${created === 1 ? 'kopia' : 'kopior'} skapades.`,
      });
    } catch (error) {
      setResult({
        message: `Något gick fel efter ${created} skapade kopior. Försök igen.`,
        negative: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <MyModal title="Kopiera över flera dagar" onClose={onClose}>
      <Info>
        {includeWeekends
          ? 'Skapar kopior av uppdraget per dag i intervallet, inklusive helgdagar — utan fälttestare, så de kan planeras in separat utan schemakrockar.'
          : 'Skapar kopior av uppdraget per vardag (mån–fre) i intervallet — utan fälttestare, så de kan planeras in separat utan schemakrockar. Helger hoppas över.'}
      </Info>
      <LabelWrap label="Datumspann">
        <DatePicker
          calendarStartDay={1}
          customInput={<DateRangeInput daterange={range} />}
          dateFormat="yyyy-MM-dd"
          endDate={range[1]}
          locale="sv"
          onChange={(r) => {
            setRange([adjustDateToNoon(r[0]), adjustDateToNoon(r[1])]);
          }}
          populateHolidays
          selected={range[0]}
          selectsRange
          showWeekNumbers
          startDate={range[0]}
        />
      </LabelWrap>
      {hasMultipleLegs && (
        <Checkbox
          checked={copyAllLegs}
          onChange={(e) => setCopyAllLegs(e.currentTarget.checked)}
        >
          Kopiera samtliga uppdrag i ärendet ({legsToCopyAll.length} st)
        </Checkbox>
      )}
      <LabelWrap label="Antal kopior per dag">
        <NumberInput
          type="number"
          min={1}
          value={copiesPerDay}
          onChange={(e) =>
            setCopiesPerDay(
              Math.max(1, Math.floor(Number(e.currentTarget.value) || 1))
            )
          }
        />
      </LabelWrap>
      {isZakraHubCase && (
        <LabelWrap label="Anledning till Zäkra-hub som företag">
          <AutoSizedTextArea
            value={zakraHubReason}
            onChange={(e) => {
              setZakraHubReason((e.target as HTMLTextAreaElement).value);
            }}
            placeholder="Kopiorna hamnar på en Zäkra-hub och kan inte faktureras — förklara varför..."
          />
        </LabelWrap>
      )}
      <Info>
        {targetDays.length > 0
          ? `${totalCopies} ${totalCopies === 1 ? 'kopia' : 'kopior'} skapas (${targetDays.length} ${includeWeekends ? 'dag(ar)' : 'vardag(ar)'} × ${copiesPerDay} per dag).`
          : `Inga ${includeWeekends ? 'dagar' : 'vardagar'} i det valda intervallet.`}
      </Info>
      {result && (
        <ResultText negative={result.negative}>{result.message}</ResultText>
      )}
      <Button
        type="button"
        disabled={
          totalCopies === 0 ||
          isCreating ||
          (isZakraHubCase && !zakraHubReason.trim())
        }
        onClick={handleCreate}
      >
        {isCreating
          ? 'Kopierar...'
          : `Skapa ${totalCopies > 0 ? `${totalCopies} ` : ''}kopior`}
      </Button>
    </MyModal>
  );
};

export default CopyOverMultipleDaysModal;
